// FullCalendarEnterpriseTheme.tsx
import { css } from '@emotion/react';
import { alpha, darken, lighten } from '@mui/material/styles';
import { controlRadius, fcEventRadius } from './tokens';

const makeTone = (color, mode, aLight = 0.14, aDark = 0.26) =>
  mode === 'dark' ? alpha(color, aDark) : alpha(color, aLight);

export function createFullCalendarEnterpriseCss(theme) {
  // Container/surface radius (scrollgrid, list-table) stays on the global
  // border radius. Controls and events split off onto scoped tokens (§6g).
  // Fallback 6 matches the widget base theme (defaults.jsx) and the token
  // accessors, so surfaces and controls resolve equal if borderRadius is ever
  // absent (unreachable today, but keeps the "controls == surfaces" identity).
  const radius = `${theme.shape.borderRadius ?? 6}px`;
  const controlRadiusPx = `${controlRadius(theme)}px`;
  const fcEventRadiusPx = `${fcEventRadius(theme)}px`;

  const text = theme.palette.text.primary;
  const muted = theme.palette.text.secondary;
  const divider = theme.palette.divider;

  // `white`/`black` stay as the anchors for the mode-aware TINTS below (weekend
  // shading, hover/active overlays) — they are deliberately not surfaces.
  const white = theme.palette.common.white;
  const black = theme.palette.common.black;

  // Calendar surfaces follow the theme's card colour, so the grid matches the
  // booking card it renders inside. These were pinned to `common.white`, which
  // ignored the theme outright: every dark theme drew a white rectangle inside
  // a dark card, and off-white cards (werkbank, feierabend) got a pure-white
  // grid that didn't match. Reported against the `nocturne` preset, 2026-08.
  //
  // Default-preserving: MUI's light `background.paper` is `#fff`, so any theme
  // that never set it renders byte-identically to the old output.
  const bgDefault = theme.palette.background.paper;
  const surface = theme.palette.background.paper;

  // `palette.mode` is authoritative here because DomHandler derives it from the
  // palette's own surface before createTheme (styles/themeMode.js) — a theme
  // built from dark colours no longer arrives claiming to be light. Read it
  // plainly; do NOT re-derive it locally.
  const mode = theme.palette.mode;

  // Keep palette-driven accents
  const primary = theme.palette.primary.main;
  const secondary = theme.palette.secondary?.main ?? text;
  const info = theme.palette.info.main;
  const success = theme.palette.success.main;
  const warning = theme.palette.warning.main;
  const error = theme.palette.error.main;
  const contrastOnPrimary = theme.palette.getContrastText(primary);

  const primaryHover =
    mode === 'dark' ? lighten(primary, 0.08) : darken(primary, 0.08);
  const primaryActive =
    mode === 'dark' ? lighten(primary, 0.16) : darken(primary, 0.16);

  // These are no longer used for surface shading, but kept for event tints etc.
  const surfaceSubtle = 'transparent';
  const surfaceRaised = 'transparent';

  const slotHeight = theme.spacing(6); // adjust to taste, e.g., theme.spacing(4.5)

  const todayBg = makeTone(primary, mode, 0.14, 0.28);
  const weekendBg = mode === 'dark' ? alpha(white, 0.025) : alpha(black, 0.03);
  const highlight = makeTone(primary, mode, 0.18, 0.26);
  const nowColor = error;

  // Event overlays (lighter on hover, a touch stronger on press)
  const hoverOverlayDefault =
    mode === 'dark' ? alpha(white, 0.08) : alpha(black, 0.06);
  const activeOverlayDefault =
    mode === 'dark' ? alpha(white, 0.14) : alpha(black, 0.1);

  // Category gradient helper (soft tint)
  const grad = (c) =>
    `linear-gradient(180deg, ${makeTone(c, mode, 0.18, 0.3)} 0%, ${makeTone(c, mode, 0.1, 0.2)} 100%)`;

  return css`
    .fc.fc-theme-standard {
      --fc-page-bg-color: ${bgDefault};
      --fc-border-color: ${divider};
      --fc-neutral-text-color: ${muted};

      --fc-button-text-color: ${contrastOnPrimary};
      --fc-button-bg-color: ${primary};
      --fc-button-border-color: ${primary};
      --fc-button-hover-bg-color: ${primaryHover};
      --fc-button-active-bg-color: ${primaryActive};

      --fc-today-bg-color: ${todayBg};
      --fc-now-indicator-color: ${nowColor};

      --fc-event-bg-color: ${mode === 'dark' ? alpha(black, 0.24) : surface};
      --fc-event-border-color: ${alpha(text, 0.08)};
      --fc-event-text-color: ${text};

      --fc-surface: ${surface};
      --fc-surface-subtle: ${surfaceSubtle};
      --fc-surface-raised: ${surfaceRaised};
      --fc-weekend-bg: ${weekendBg};

      --radius: ${radius};
      --control-radius: ${controlRadiusPx};
      --fc-event-radius: ${fcEventRadiusPx};
      --shadow-1: ${theme.shadows[1]};
      --shadow-2: ${theme.shadows[2]};
      --shadow-3: ${theme.shadows[3]};

      --slot-height: ${slotHeight};

      --fc-today-bg-color: transparent;

      font-family: ${theme.typography.fontFamily};
      color: ${text};
    }

    /* Toolbar: card surface, segmented controls, no outer focus ring */
    .fc.fc-theme-standard .fc-toolbar {
      display: flex; /* flex instead of grid for robustness */
      align-items: center;
      gap: 12px;
      flex-wrap: nowrap;
      margin-bottom: 14px;
      padding: 12px;
      background: var(--fc-surface);
      border: 1px solid var(--fc-border-color);
      /* Surface, not a control: use --radius so a pill control-radius (999)
         doesn't turn this bordered/backed bar into a stadium. Controls
         (.fc-button, .fc-button-group, day-number chip) keep --control-radius. */
      border-radius: var(--radius);
    }

    /* Keep title styling */
    .fc.fc-theme-standard .fc-toolbar-title {
      font-weight: 600;
      letter-spacing: -0.01em;
      font-size: clamp(1.02rem, 1.1vw, 1.18rem);
    }
    .fc.fc-theme-standard .fc-button-group {
      display: inline-flex;
      gap: 0;
      border-radius: var(--control-radius);
      overflow: hidden;
      border: 1px solid ${alpha(primary, 0.35)};
      background: transparent; /* buttons sit directly on the surface */
    }
    .fc.fc-theme-standard .fc-button {
      position: relative;
      isolation: isolate;
      overflow: hidden;
      border: 0;
      border-radius: var(--control-radius);
      padding: 8px 14px;
      line-height: 1;
      font-weight: 500;
      background: transparent;
      color: ${primary};
      transition:
        background-color 0.15s ease,
        color 0.15s ease,
        transform 0.06s ease,
        box-shadow 0.15s ease;
      box-shadow: none;
      text-transform: inherit;
    }
    .fc.fc-theme-standard .fc-button:hover {
      background: ${alpha(primary, mode === 'dark' ? 0.18 : 0.12)};
    }
    .fc.fc-theme-standard .fc-button:focus,
    .fc.fc-theme-standard .fc-button:focus-visible {
      outline: 0;
      box-shadow: none; /* remove strange focus border */
    }
    .fc.fc-theme-standard .fc-button::after {
      content: '';
      position: absolute;
      inset: 0;
      border-radius: inherit;
      background: ${alpha(primary, mode === 'dark' ? 0.2 : 0.14)};
      opacity: 0;
      transition: opacity 120ms ease;
      pointer-events: none;
      z-index: 1;
    }
    .fc.fc-theme-standard .fc-button:active::after {
      opacity: 1;
    }
    .fc.fc-theme-standard .fc-button:active {
      transform: translateY(0px);
    }
    .fc.fc-theme-standard .fc-button.fc-button-active {
      background: ${primary};
      color: ${contrastOnPrimary};
    }

    .fc.fc-theme-standard .fc-button-primary:not(:disabled):active:focus {
      box-shadow: none;
    }

    /* Grid container + header: card surfaces */
    .fc.fc-theme-standard .fc-scrollgrid {
      border-radius: var(--radius);
      overflow: hidden;
      background: var(--fc-surface);
      box-shadow: var(--shadow-1);
    }
    .fc.fc-theme-standard .fc-col-header-cell {
      background: var(--fc-surface);
      color: ${muted};
      text-transform: uppercase;
      letter-spacing: 0.06em;
      font-size: 0.7rem; /* smaller */
      font-weight: 500; /* lighter */
    }
    .fc.fc-theme-standard .fc-col-header-cell-cushion {
      padding: 8px 10px;
    }

    /* DayGrid weekend shading (optional) */
    .fc.fc-theme-standard[data-shade-weekends='true']
      .fc-day-sat
      .fc-daygrid-day-frame,
    .fc.fc-theme-standard[data-shade-weekends='true']
      .fc-day-sun
      .fc-daygrid-day-frame {
      background: var(--fc-weekend-bg);
    }

    /* Day number chips */
    .fc.fc-theme-standard .fc-daygrid-day-top {
      padding: 8px 10px 4px 10px;
    }
    .fc.fc-theme-standard .fc-daygrid-day-number {
      color: ${text};
      font-weight: 600;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      min-width: 2rem;
      height: 2rem;
      border-radius: var(--control-radius); /* day-number chip = control */
      transition:
        background-color 0.12s ease,
        color 0.12s ease;
    }
    .fc.fc-theme-standard .fc-daygrid-day:hover .fc-daygrid-day-number {
      background: ${alpha(text, 0.06)};
    }
    .fc.fc-theme-standard .fc-day-today .fc-daygrid-day-number {
      background: var(--fc-today-bg-color);
      color: ${primary};
    }

    /* Selection + now indicator */
    .fc.fc-theme-standard .fc-highlight {
      background: ${highlight};
    }
    .fc.fc-theme-standard .fc-timegrid-now-indicator-line {
      border-color: var(--fc-now-indicator-color);
      box-shadow: 0 0 0 1px ${alpha(nowColor, 0.32)};
    }

    .fc.fc-theme-standard .fc-timegrid-slot {
      height: var(--slot-height);
    }

    /* TimeGrid lanes: REMOVE horizontal lines as requested */
    .fc.fc-theme-standard .fc-timegrid-slot,
    .fc.fc-theme-standard .fc-timegrid-slot-lane,
    .fc.fc-theme-standard .fc-timegrid-slot-minor {
      border-top-width: 0 !important;
      border-top-style: none !important;
      border-top-color: transparent !important;
    }

    /* Time axis labels (left): make a bit smaller */
    .fc.fc-theme-standard .fc-timegrid-axis-cushion,
    .fc.fc-theme-standard .fc-timegrid-slot-label-cushion {
      font-size: 0.75rem; /* smaller */
      color: ${muted};
    }

    /* Events: unified look (timeGrid + dayGrid) with accent bar + tint */
    .fc.fc-theme-standard .fc-event {
      border: 1px solid var(--event-border, var(--fc-event-border-color));
      /* fcEventRadius caps at 12 (only when slotRadius is set) because the
         ::before accent bar below is 4px wide under overflow: hidden — a
         fully-round radius would clip the bar. */
      border-radius: var(--fc-event-radius);
      background: var(--event-bg, var(--fc-event-bg-color));
      color: var(--event-text, var(--fc-event-text-color));
      overflow: hidden;
      position: relative;
      text-decoration: none;
    }
    .fc.fc-theme-standard .fc-event::before {
      content: '';
      position: absolute;
      inset: 0 auto 0 0;
      width: 4px;
      background: var(--event-accent, ${primary});
      opacity: 0.95;
    }
    .fc.fc-theme-standard .fc-event .fc-event-main {
      padding: 8px 10px;
      font-weight: 500;
    }
    .fc.fc-theme-standard .fc-daygrid-dot-event .fc-event-title {
      font-weight: 600;
    }
    .fc.fc-theme-standard .fc-daygrid-dot-event .fc-event-dot {
      border-color: currentColor;
    }

    /* TimeGridWeek: hover + pressed states (no lingering dark focus) */
    .fc.fc-theme-standard .fc-timegrid-event {
      position: relative;
      isolation: isolate;
      overflow: hidden; /* clips overlays to the event shape */
      transition:
        box-shadow 140ms ease,
        transform 120ms ease;
      background-clip: padding-box; /* keep overlays bounded cleanly */
    }
    .fc.fc-theme-standard .fc-timegrid-event::after {
      content: '';
      position: absolute;
      inset: 0;
      border-radius: inherit;
      background: var(--event-overlay-hover, ${hoverOverlayDefault});
      opacity: 0;
      transition: opacity 140ms ease;
      pointer-events: none;
      z-index: 1;
    }
    .fc.fc-theme-standard .fc-timegrid-event:hover {
      box-shadow: var(--shadow-2);
      transform: translateY(-1px);
    }
    .fc.fc-theme-standard .fc-timegrid-event:hover::after {
      opacity: 1;
    }
    .fc.fc-theme-standard .fc-timegrid-event:active::after {
      opacity: 1;
      background: var(--event-overlay-active, ${activeOverlayDefault});
      transition-duration: 60ms;
    }
    .fc.fc-theme-standard .fc-timegrid-event:focus,
    .fc.fc-theme-standard .fc-timegrid-event:focus-visible,
    .fc.fc-theme-standard .fc-event:focus,
    .fc.fc-theme-standard .fc-event:focus-visible {
      outline: 0;
      background: var(--event-bg, var(--fc-event-bg-color));
    }
    .fc.fc-theme-standard .fc-event.fc-event-selected {
      background: var(--event-bg, var(--fc-event-bg-color));
    }

    /* Category tints + overlay colors */
    .fc.fc-theme-standard .fc-event[data-cat='meeting'] {
      --event-accent: ${primary};
      --event-border: ${alpha(primary, 0.5)};
      --event-bg: ${grad(primary)};
      --event-text: ${primary};
      --event-overlay-hover: ${alpha(primary, mode === 'dark' ? 0.16 : 0.1)};
      --event-overlay-active: ${alpha(primary, mode === 'dark' ? 0.22 : 0.14)};
    }
    .fc.fc-theme-standard .fc-event[data-cat='travel'] {
      --event-accent: ${info};
      --event-border: ${alpha(info, 0.5)};
      --event-bg: ${grad(info)};
      --event-text: ${info};
      --event-overlay-hover: ${alpha(info, mode === 'dark' ? 0.16 : 0.1)};
      --event-overlay-active: ${alpha(info, mode === 'dark' ? 0.22 : 0.14)};
    }
    .fc.fc-theme-standard .fc-event[data-cat='focus'] {
      --event-accent: ${secondary};
      --event-border: ${alpha(secondary, 0.5)};
      --event-bg: ${grad(secondary)};
      --event-text: ${secondary};
      --event-overlay-hover: ${alpha(secondary, mode === 'dark' ? 0.16 : 0.1)};
      --event-overlay-active: ${alpha(
        secondary,
        mode === 'dark' ? 0.22 : 0.14,
      )};
    }
    .fc.fc-theme-standard .fc-event[data-cat='outofoffice'] {
      --event-accent: ${error};
      --event-border: ${alpha(error, 0.5)};
      --event-bg: ${grad(error)};
      --event-text: ${error};
      --event-overlay-hover: ${alpha(error, mode === 'dark' ? 0.16 : 0.1)};
      --event-overlay-active: ${alpha(error, mode === 'dark' ? 0.22 : 0.14)};
    }
    .fc.fc-theme-standard .fc-event[data-cat='personal'] {
      --event-accent: ${success};
      --event-border: ${alpha(success, 0.5)};
      --event-bg: ${grad(success)};
      --event-text: ${success};
      --event-overlay-hover: ${alpha(success, mode === 'dark' ? 0.16 : 0.1)};
      --event-overlay-active: ${alpha(success, mode === 'dark' ? 0.22 : 0.14)};
    }
    .fc.fc-theme-standard .fc-event[data-cat='training'],
    .fc.fc-theme-standard .fc-event[data-cat='warning'] {
      --event-accent: ${warning};
      --event-border: ${alpha(warning, 0.5)};
      --event-bg: ${grad(warning)};
      --event-text: ${warning};
      --event-overlay-hover: ${alpha(warning, mode === 'dark' ? 0.16 : 0.1)};
      --event-overlay-active: ${alpha(warning, mode === 'dark' ? 0.22 : 0.14)};
    }

    /* All-day + list view on the card surface */
    .fc.fc-theme-standard .fc-h-event {
      border-radius: var(--fc-event-radius);
    }
    .fc.fc-theme-standard .fc-list,
    .fc.fc-theme-standard .fc-list-sticky {
      border: 0;
    }
    .fc.fc-theme-standard .fc-list-table {
      background: var(--fc-surface);
      border-radius: var(--radius);
      overflow: hidden;
      border: 1px solid var(--fc-border-color);
      box-shadow: var(--shadow-1);
    }
    .fc.fc-theme-standard .fc-list-day-cushion {
      background: var(--fc-surface);
      font-weight: 600;
    }
    .fc.fc-theme-standard .fc-list-event:hover td {
      background: ${theme.palette.action.hover};
    }

    /* Density + responsive */
    .fc.fc-theme-standard[data-density='compact'] .fc-toolbar {
      padding: 8px 10px;
      margin-bottom: 10px;
    }
    .fc.fc-theme-standard[data-density='compact'] .fc-button {
      padding: 6px 10px;
    }
    .fc.fc-theme-standard[data-density='compact'] .fc-daygrid-day-number {
      min-width: 1.6rem;
      height: 1.6rem;
    }
    .fc.fc-theme-standard[data-density='compact'] .fc-event .fc-event-main {
      padding: 6px 8px;
    }

    /* Each chunk is a mini-flex row */
    .fc.fc-theme-standard .fc-toolbar .fc-toolbar-chunk {
      display: flex;
      align-items: center;
      gap: 8px;
    }

    /* Robust alignment regardless of how many chunks FullCalendar renders */
    .fc.fc-theme-standard .fc-toolbar .fc-toolbar-chunk:first-child {
      margin-right: auto; /* left chunk sticks left */
    }
    .fc.fc-theme-standard .fc-toolbar .fc-toolbar-chunk:last-child {
      margin-left: auto; /* right chunk sticks right (even if center is absent) */
    }

    /* Mobile: stack chunks cleanly */
    @media (max-width: 767px) {
      .fc.fc-theme-standard .fc-toolbar {
        flex-wrap: wrap;
        gap: 8px;
      }
      .fc.fc-theme-standard .fc-toolbar .fc-toolbar-chunk {
        flex: 1 1 100%;
        justify-content: space-between;
      }
    }

    @media (max-width: 767px) {
      .fc.fc-theme-standard .fc-toolbar {
        grid-template-columns: 1fr;
      }
    }
    @media (prefers-reduced-motion: reduce) {
      .fc.fc-theme-standard .fc-button,
      .fc.fc-theme-standard .fc-timegrid-event,
      .fc.fc-theme-standard .fc-event {
        transition: none !important;
      }
    }
  `;
}
createFullCalendarEnterpriseCss.displayName = 'FullCalendar Enterprise CSS';

export default createFullCalendarEnterpriseCss;
